/**
 * GA4 e-commerce funnel helper.
 *
 * All events are only fired when `window.gtag` is available (loaded from index.html).
 * Consent Mode v2 handles the "denied" case natively: gtag sends cookieless pings
 * or drops the hit entirely depending on category. We don't need to guard against
 * denied consent here — the user's choice flows through `saveConsent()`.
 */

const KIND_TO_CATEGORY = {
    subscription: "subscription",
    event: "event",
    booking: "care_session",
    program: "program",
    extra: "extra_session",
    gift: "gift",
};

function _gtag(...args) {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag(...args);
}

/**
 * Convert internal `item` (from PaymentMethodDialog) into a GA4 `items` array.
 * item shape: { kind, lookup_key?, event_id?, booking_id?, program_key?, extra_key?, amount?, item_name }
 */
function _buildItems(item) {
    if (!item) return [];
    const id =
        item.lookup_key || item.event_id || item.booking_id ||
        item.program_key || item.extra_key || item.item_name || "unknown";
    return [{
        item_id: String(id),
        item_name: item.item_name || String(id),
        item_category: KIND_TO_CATEGORY[item.kind] || item.kind || "unknown",
        price: (item.amount || 0) / 100,
        quantity: 1,
    }];
}

function _value(item) {
    return (item?.amount || 0) / 100;
}

/** User opened the payment modal → declared interest in a product. */
export function trackSelectItem(item) {
    if (!item) return;
    _gtag("event", "select_item", {
        currency: "EUR",
        value: _value(item),
        items: _buildItems(item),
    });
}

/**
 * User clicked a payment method (Stripe / PayPal / bank transfer) → checkout started.
 * Fires both `begin_checkout` and `add_payment_info` (GA4 recommends both for full funnel).
 * payment_type ∈ "card" | "paypal" | "bank_transfer"
 */
export function trackBeginCheckout(item, paymentType) {
    if (!item) return;
    const payload = {
        currency: "EUR",
        value: _value(item),
        items: _buildItems(item),
    };
    _gtag("event", "begin_checkout", payload);
    _gtag("event", "add_payment_info", { ...payload, payment_type: paymentType });
}

/**
 * Purchase confirmed. Uses sessionStorage to deduplicate — GA4 requires unique
 * transaction_id to avoid double-counting when the user reloads /payment/success.
 */
export function trackPurchase(transactionId, item) {
    if (!transactionId || !item) return;
    const key = `fol_ga_purchase_${transactionId}`;
    try {
        if (sessionStorage.getItem(key) === "1") return; // already tracked this reload cycle
        sessionStorage.setItem(key, "1");
    } catch {}
    _gtag("event", "purchase", {
        transaction_id: String(transactionId),
        currency: "EUR",
        value: _value(item),
        items: _buildItems(item),
    });
}

/** Newsletter subscription success. */
export function trackLead(source = "newsletter") {
    _gtag("event", "generate_lead", {
        currency: "EUR",
        value: 0,
        method: source,
    });
}
